import React, { useState, useEffect } from "react";
import "./DevDocs.css";

const DevDocs = () => {
  const [activeSection, setActiveSection] = useState("intro");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const sections = [
    { id: "intro", title: "Introduction", number: "1" },
    { id: "requirements", title: "Requirements", number: "2" },
    { id: "git", title: "Git", number: "2.1", parent: "requirements" },
    {
      id: "github-desktop",
      title: "GitHub Desktop",
      number: "2.2",
      parent: "requirements",
    },
    { id: "java", title: "Java JDK", number: "2.3", parent: "requirements" },
    { id: "nodejs", title: "Node.js", number: "2.4", parent: "requirements" },
    { id: "expo", title: "Expo CLI", number: "2.5", parent: "requirements" },
    {
      id: "android",
      title: "Android Studio",
      number: "2.6",
      parent: "requirements",
    },
    { id: "ides", title: "IDEs", number: "2.7", parent: "requirements" },
    { id: "clone", title: "Clone Repositories", number: "3" },
    { id: "backend", title: "Backend Setup", number: "4" },
    {
      id: "backend-env",
      title: "Environment Variables",
      number: "4.1",
      parent: "backend",
    },
    {
      id: "backend-mysql",
      title: "MySQL Setup",
      number: "4.2",
      parent: "backend",
    },
    {
      id: "backend-migrations",
      title: "Database Migrations",
      number: "4.3",
      parent: "backend",
    },
    {
      id: "backend-run",
      title: "Run Backend",
      number: "4.4",
      parent: "backend",
    },
    { id: "frontend", title: "Frontend Setup", number: "5" },
    {
      id: "frontend-install",
      title: "Install Packages",
      number: "5.1",
      parent: "frontend",
    },
    {
      id: "frontend-url",
      title: "Backend URL Config",
      number: "5.2",
      parent: "frontend",
    },
    {
      id: "frontend-run",
      title: "Run Frontend",
      number: "5.3",
      parent: "frontend",
    },
    { id: "workflow", title: "Practice Workflow", number: "6" },
    { id: "troubleshooting", title: "Troubleshooting", number: "7" },
    { id: "team-rules", title: "Team Rules", number: "8" },
    { id: "ask-team", title: "What to Ask", number: "9" },
  ];

  const handleNavClick = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="dev-docs-container">
      <aside className={`dev-docs-sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          <h2>GoalFlex</h2>
          <p>Developer Setup Guide</p>
        </div>
        <nav className="sidebar-nav">
          {sections.map((section) => (
            <div
              key={section.id}
              className={`nav-item ${section.parent ? "nav-item-child" : ""} ${
                activeSection === section.id ? "active" : ""
              }`}
              onClick={() => handleNavClick(section.id)}
            >
              <span className="nav-number">{section.number}</span>
              <span className="nav-title">{section.title}</span>
            </div>
          ))}
        </nav>
      </aside>

      <button
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
      >
        {sidebarOpen ? "✕" : "☰"}
      </button>

      <main
        className={`dev-docs-content ${sidebarOpen ? "" : "sidebar-closed"}`}
      >
        <section id="intro" className="doc-section">
          <h1>GoalFlex – Windows Developer Setup</h1>
          <p className="subtitle">
            Backend (Spring Boot) + Frontend (React Native / Expo)
          </p>
          <p>
            This documentation is for Windows teammates to run the Spring Boot
            backend and the React Native (Expo) app locally, make changes, and
            test safely.
          </p>

          <div className="info-box">
            <h3>What you will be able to do</h3>
            <p>After completing this setup, you can:</p>
            <ul>
              <li>
                Clone the repos and open them in IntelliJ (backend) and VS
                Code/IntelliJ (frontend)
              </li>
              <li>
                Run a local MySQL database (recommended via Docker Desktop)
              </li>
              <li>
                Run the Spring Boot backend on{" "}
                <code>http://localhost:8080</code>
              </li>
              <li>
                Run the React Native app on an Android emulator or Android phone
              </li>
              <li>
                Change code (frontend/backend), re-run, and verify behavior
                end-to-end
              </li>
            </ul>
          </div>
        </section>

        <section id="requirements" className="doc-section">
          <h2>Requirements</h2>
          <p>
            Install the tools below in this order. Each tool includes: where to
            install, how to verify, and any notes.
          </p>
        </section>

        <section id="git" className="doc-section subsection">
          <h3>2.1 Git (required)</h3>
          <div className="install-block">
            <p>
              <strong>Install:</strong> Git for Windows
            </p>
            <p>
              <strong>Download:</strong>{" "}
              <a
                href="https://git-scm.com/download/win"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://git-scm.com/download/win
              </a>
            </p>
            <p>
              <strong>Verify:</strong>
            </p>
            <pre>
              <code>git --version</code>
            </pre>
          </div>
        </section>

        <section id="github-desktop" className="doc-section subsection">
          <h3>2.2 GitHub Desktop (recommended)</h3>
          <div className="install-block">
            <p>
              <strong>Install:</strong> GitHub Desktop for Windows
            </p>
            <p>
              <strong>Download:</strong>{" "}
              <a
                href="https://desktop.github.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://desktop.github.com/
              </a>
            </p>
            <p>
              GitHub Desktop provides a visual interface for Git operations,
              making it easier to:
            </p>
            <ul>
              <li>Clone repositories</li>
              <li>Create and switch branches</li>
              <li>Commit and push changes</li>
              <li>View diffs and history</li>
              <li>Manage pull requests</li>
            </ul>
            <p className="note">
              Sign in with your GitHub account that has access to the GoalFlex
              repositories
            </p>
          </div>
        </section>

        <section id="java" className="doc-section subsection">
          <h3>2.3 Java JDK (required for Spring Boot)</h3>
          <div className="install-block">
            <p>
              <strong>Install:</strong> JDK 17 (recommended) unless the project
              explicitly uses 21
            </p>
            <p>
              <strong>Download (Temurin / Adoptium):</strong>{" "}
              <a
                href="https://adoptium.net/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://adoptium.net/
              </a>
            </p>
            <p>
              During install: check the option to add Java to PATH if it
              appears.
            </p>
            <p>
              <strong>Verify:</strong>
            </p>
            <pre>
              <code>java -version</code>
            </pre>
            <p className="note">
              Expected: shows Java 17.x (or 21.x if your team standard is 21)
            </p>
          </div>
        </section>

        <section id="nodejs" className="doc-section subsection">
          <h3>2.4 Node.js (required for React Native / Expo)</h3>
          <div className="install-block">
            <p>
              <strong>Install:</strong> Node.js LTS (recommended)
            </p>
            <p>
              <strong>Download:</strong>{" "}
              <a
                href="https://nodejs.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://nodejs.org/
              </a>
            </p>
            <p>
              <strong>Verify:</strong>
            </p>
            <pre>
              <code>node -v{"\n"}npm -v</code>
            </pre>
          </div>
        </section>

        <section id="expo" className="doc-section subsection">
          <h3>2.5 Expo CLI (for React Native app)</h3>
          <div className="install-block">
            <p>
              <strong>Install (global):</strong>
            </p>
            <pre>
              <code>npm install -g expo</code>
            </pre>
            <p>
              <strong>Verify:</strong>
            </p>
            <pre>
              <code>expo --version</code>
            </pre>
          </div>
        </section>

        <section id="android" className="doc-section subsection">
          <h3>2.6 Android Studio (for emulator) + Android SDK</h3>
          <div className="install-block">
            <p>
              <strong>Install:</strong> Android Studio
            </p>
            <p>
              <strong>Download:</strong>{" "}
              <a
                href="https://developer.android.com/studio"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://developer.android.com/studio
              </a>
            </p>
            <p>
              <strong>Inside Android Studio:</strong>
            </p>
            <ol>
              <li>
                Open Android Studio → Settings (or Preferences) → Appearance &
                Behavior → System Settings → Android SDK
              </li>
              <li>
                Install an Android SDK Platform (pick a recent stable one)
              </li>
              <li>Install Android SDK Build-Tools and Platform-Tools</li>
              <li>
                Create an emulator: Tools → Device Manager → Create Device
              </li>
            </ol>
            <p>
              <strong>Verify ADB (optional but useful):</strong>
            </p>
            <pre>
              <code>adb version</code>
            </pre>
            <p className="note">
              If adb is not found, add the Android 'platform-tools' directory to
              your PATH (Android Studio shows the SDK path)
            </p>
          </div>
        </section>

        <section id="ides" className="doc-section subsection">
          <h3>2.7 IDEs (recommended)</h3>
          <div className="install-block">
            <p>
              <strong>Backend:</strong> IntelliJ IDEA
            </p>
            <p>
              <strong>Frontend:</strong> VS Code or IntelliJ
            </p>
            <p>
              <strong>Optional tools:</strong>
            </p>
            <ul>
              <li>
                Postman (API testing):{" "}
                <a
                  href="https://www.postman.com/downloads/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://www.postman.com/downloads/
                </a>
              </li>
              <li>
                MySQL Workbench (DB GUI):{" "}
                <a
                  href="https://dev.mysql.com/downloads/workbench/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://dev.mysql.com/downloads/workbench/
                </a>
              </li>
            </ul>
          </div>
        </section>

        <section id="clone" className="doc-section">
          <h2>Get the Code (Clone Repos)</h2>
          <p>You will need access to the GitHub organization repo(s).</p>
          <p>In a terminal (PowerShell or Git Bash):</p>
          <div className="install-block">
            <p>
              <strong>Backend Repository:</strong>
            </p>
            <pre>
              <code>
                git clone https://github.com/AbdulhakimEdao/GoalFlex-Backend.git
              </code>
            </pre>
            <p>
              <strong>Frontend Repository (Web):</strong>
            </p>
            <pre>
              <code>
                git clone https://github.com/AbdulhakimEdao/Goalflex-Webpage.git
              </code>
            </pre>
            <p>
              <strong>Mobile App Repository (React Native):</strong>
            </p>
            <pre>
              <code>
                git clone https://github.com/AbdulhakimEdao/GoalFlex-App.git
              </code>
            </pre>
          </div>
        </section>

        <section id="backend" className="doc-section">
          <h2>Backend Setup (Spring Boot)</h2>
        </section>

        <section id="backend-env" className="doc-section subsection">
          <h3>4.1 Configure Environment Variables</h3>
          <p>
            Create a local env file (do not commit secrets). Use the project's
            template if available (example: <code>.env.example</code>).
          </p>
          <div className="install-block">
            <p>
              <strong>Create:</strong>
            </p>
            <pre>
              <code>copy .env.example .env</code>
            </pre>
            <p>
              If there is no template, ask the team for required keys. Typical
              keys:
            </p>
            <ul>
              <li>
                <code>DB_URL</code>, <code>DB_USERNAME</code>,{" "}
                <code>DB_PASSWORD</code>
              </li>
              <li>
                <code>JWT_SECRET</code>
              </li>
              <li>
                <code>AWS_REGION</code> (if needed for S3/SES)
              </li>
              <li>Any Stripe keys (usually only for dev)</li>
            </ul>
          </div>
        </section>

        <section id="backend-mysql" className="doc-section subsection">
          <h3>4.2 MySQL Database Setup</h3>
          <p>You'll need access to a MySQL database. Contact the team for:</p>
          <div className="install-block">
            <ul>
              <li>Database host and port</li>
              <li>Database name</li>
              <li>Database username and password</li>
              <li>Any required connection parameters</li>
            </ul>
            <p className="note">
              The team will provide you with the development database
              credentials. Add these to your <code>.env</code> file.
            </p>
          </div>
        </section>

        <section id="backend-migrations" className="doc-section subsection">
          <h3>4.3 Run Database Migrations / Seed Data</h3>
          <p>Depending on your backend:</p>
          <ul>
            <li>
              If you use Flyway/Liquibase: starting the app may auto-run
              migrations
            </li>
            <li>
              If you need to run a SQL dump: ask the team for the latest
              database dump file
            </li>
          </ul>
          <p className="note">
            Contact the team if you need help with database setup or migrations
          </p>
        </section>

        <section id="backend-run" className="doc-section subsection">
          <h3>4.4 Run the Backend</h3>
          <p>From the backend folder:</p>
          <div className="install-block">
            <p>
              <strong>If Maven Wrapper exists (recommended):</strong>
            </p>
            <pre>
              <code>.\mvnw.cmd clean spring-boot:run</code>
            </pre>
            <p>
              <strong>Or build and run the JAR:</strong>
            </p>
            <pre>
              <code>
                .\mvnw.cmd clean package{"\n"}java -jar
                target\&lt;YOUR_JAR_NAME&gt;.jar
              </code>
            </pre>
            <p>
              <strong>Verify backend is up:</strong>
            </p>
            <pre>
              <code>curl http://localhost:8080/actuator/health</code>
            </pre>
            <p className="note">
              If actuator is not enabled, verify a known endpoint, or open
              http://localhost:8080 in a browser
            </p>
          </div>
        </section>

        <section id="frontend" className="doc-section">
          <h2>Frontend Setup (React Native – Expo)</h2>
        </section>

        <section id="frontend-install" className="doc-section subsection">
          <h3>5.1 Install Packages</h3>
          <p>From the frontend folder:</p>
          <pre>
            <code>npm install</code>
          </pre>
          <p className="note">
            If your repo uses yarn/pnpm, follow the repo's lockfile: yarn.lock
            or pnpm-lock.yaml
          </p>
        </section>

        <section id="frontend-url" className="doc-section subsection">
          <h3>5.2 Set Backend URL</h3>
          <p>The app must point to your local backend.</p>
          <p>
            <strong>Common options:</strong>
          </p>
          <ul>
            <li>
              Set an environment variable (e.g.,{" "}
              <code>EXPO_PUBLIC_API_BASE_URL=http://10.0.2.2:8080</code> for
              Android emulator)
            </li>
            <li>
              Use an app config file (e.g., <code>app.config.js</code> /{" "}
              <code>app.json</code>)
            </li>
          </ul>
          <div className="warning-box">
            <h4>⚠️ Important Android networking note:</h4>
            <ul>
              <li>
                Android emulator uses <code>10.0.2.2</code> to reach your PC's
                localhost
              </li>
              <li>
                So use: <code>http://10.0.2.2:8080</code> (not{" "}
                <code>http://localhost:8080</code>) inside the mobile app
              </li>
            </ul>
          </div>
          <div className="install-block">
            <p>
              <strong>Example (Expo public env):</strong>
            </p>
            <pre>
              <code>setx EXPO_PUBLIC_API_BASE_URL http://10.0.2.2:8080</code>
            </pre>
          </div>
        </section>

        <section id="frontend-run" className="doc-section subsection">
          <h3>5.3 Run the App</h3>
          <div className="install-block">
            <p>
              <strong>Start Metro bundler:</strong>
            </p>
            <pre>
              <code>npx expo start</code>
            </pre>
            <p>
              <strong>Then:</strong>
            </p>
            <ul>
              <li>Press 'a' in the terminal to launch Android emulator</li>
              <li>Or scan QR using Expo Go on an Android phone (same Wi-Fi)</li>
            </ul>
          </div>
        </section>

        <section id="workflow" className="doc-section">
          <h2>Quick Practice Workflow</h2>
          <p>
            Use this small practice loop to confirm your environment is correct:
          </p>
          <ol>
            <li>
              <strong>Backend:</strong> Change a simple response in a controller
              (example: <code>/api/health</code> returns a different message)
            </li>
            <li>Restart backend (or rely on devtools if enabled)</li>
            <li>
              <strong>Frontend:</strong> Call that endpoint and display the
              value on a screen
            </li>
            <li>Run the app and confirm you see the updated response</li>
          </ol>
        </section>

        <section id="troubleshooting" className="doc-section">
          <h2>Troubleshooting (Common Issues)</h2>

          <div className="troubleshoot-item">
            <h3>7.1 Port 8080 already in use</h3>
            <p>Find which process is using 8080:</p>
            <pre>
              <code>netstat -ano | findstr :8080</code>
            </pre>
            <p>Kill it (replace PID):</p>
            <pre>
              <code>taskkill /PID &lt;PID&gt; /F</code>
            </pre>
          </div>

          <div className="troubleshoot-item">
            <h3>7.2 Backend cannot connect to MySQL</h3>
            <p>Check:</p>
            <ul>
              <li>
                Database credentials in <code>.env</code> file are correct
              </li>
              <li>Database host and port are accessible</li>
              <li>Your IP is whitelisted (if using remote database)</li>
              <li>VPN is connected (if required)</li>
            </ul>
          </div>

          <div className="troubleshoot-item">
            <h3>7.3 Android app cannot reach backend</h3>
            <p>Use the correct base URL:</p>
            <ul>
              <li>
                Android emulator → <code>http://10.0.2.2:8080</code>
              </li>
              <li>
                Physical device → use your PC's LAN IP (example:{" "}
                <code>http://192.168.x.x:8080</code>)
              </li>
            </ul>
            <p className="note">
              Also verify Windows Firewall is not blocking inbound connections
              on 8080 (rare but possible)
            </p>
          </div>

          <div className="troubleshoot-item">
            <h3>7.4 Clearing Expo local cache</h3>
            <p>If the app behaves weird after changes:</p>
            <pre>
              <code>npx expo start -c</code>
            </pre>
          </div>
        </section>

        <section id="team-rules" className="doc-section">
          <h2>Team Rules (So You Don't Break Main)</h2>
          <p>
            <strong>Recommended flow:</strong>
          </p>
          <ul>
            <li>Create a feature branch for every change</li>
            <li>Open a Pull Request to main</li>
            <li>CI must pass before merge (once enabled)</li>
            <li>No direct pushes to main (branch protection)</li>
          </ul>
          <div className="install-block">
            <p>
              <strong>Branch commands:</strong>
            </p>
            <pre>
              <code>
                git checkout -b feature/&lt;short-name&gt;{"\n"}git push -u
                origin feature/&lt;short-name&gt;
              </code>
            </pre>
          </div>
        </section>

        <section id="ask-team" className="doc-section">
          <h2>What to Ask the Team</h2>
          <p>If anything is missing, ask for:</p>
          <ul>
            <li>Exact Java version (17 vs 21)</li>
            <li>Access to repositories (if you don't have it yet)</li>
            <li>
              <code>.env.example</code> (list of required environment variables)
            </li>
            <li>Database credentials (host, port, username, password)</li>
            <li>VPN setup (if required for database access)</li>
            <li>Base API URL setup method used in the app</li>
          </ul>
        </section>

        <footer className="doc-footer">
          <p>© 2026 GoalFlex – Internal Documentation</p>
        </footer>
      </main>
    </div>
  );
};

export default DevDocs;
